import {
  type WsPush,
  type WsPushChannel,
  type WsPushMessage,
  WS_METHODS,
  type WebSocketResponse as WebSocketResponseMessage,
  WebSocketResponse,
  type WsResponse as WsResponseMessage,
  WsResponse as WsResponseSchema,
} from "@t3tools/contracts";
import { decodeUnknownJsonResult, formatSchemaError } from "@t3tools/shared/schemaJson";
import { Result, Schema } from "effect";
import {
  fetchCurrentOperatorsSession,
  isOperatorsAuthExpiredError,
  notifyOperatorsAuthExpired,
  onOperatorsAuthSessionRefreshed,
  OPERATORS_AUTH_EXPIRED_MESSAGE,
  OPERATORS_AUTH_EXPIRED_WS_CLOSE_CODE,
  OperatorsAuthExpiredError,
} from "./operatorsAuthSession";

type PushListener<C extends WsPushChannel> = (message: WsPushMessage<C>) => void;

interface PendingRequest {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

interface SubscribeOptions {
  readonly replayLatest?: boolean;
}

interface RequestOptions {
  readonly timeoutMs?: number;
}

type TransportState = "connecting" | "open" | "reconnecting" | "closed" | "disposed";

const REQUEST_TIMEOUT_MS = 60_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;
const MAX_OUTBOUND_ENVELOPE_BYTES = 16 * 1_048_576;
const MAX_OUTBOUND_QUEUE_BYTES = 16 * 1_048_576;
const MAX_OUTBOUND_QUEUE_MESSAGES = 1_000;
const MAX_SOCKET_BUFFERED_AMOUNT_BYTES = 16 * 1_048_576;
const RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000];
const decodeWsResponse = decodeUnknownJsonResult(WsResponseSchema);
const isWebSocketResponseEnvelope = Schema.is(WebSocketResponse);

const isWsPushMessage = (value: WsResponseMessage): value is WsPush =>
  "type" in value && value.type === "push";

type WsRequestErrorCode = NonNullable<WebSocketResponseMessage["error"]>["code"];

export class WsRequestError extends Error {
  constructor(
    message: string,
    readonly code?: WsRequestErrorCode,
  ) {
    super(message);
    this.name = "WsRequestError";
  }
}

export class WsConnectionClosedError extends Error {
  constructor(reason?: string) {
    super(
      reason && reason.trim().length > 0
        ? `WebSocket connection closed: ${reason}`
        : "WebSocket connection closed before the request completed.",
    );
    this.name = "WsConnectionClosedError";
  }
}

interface WsRequestEnvelope {
  id: string;
  body: {
    _tag: string;
    [key: string]: unknown;
  };
}

function asError(value: unknown, fallback: string): Error {
  if (value instanceof Error) {
    return value;
  }
  return new Error(fallback);
}

function operatorsWsUrlFromHttpUrl(rawHttpUrl: string | undefined): string | null {
  if (typeof rawHttpUrl !== "string" || rawHttpUrl.length === 0) {
    return null;
  }

  try {
    const url = new URL(rawHttpUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/ws/t3";
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
}

export class WsTransport {
  private ws: WebSocket | null = null;
  private nextId = 1;
  private readonly pending = new Map<string, PendingRequest>();
  private readonly listeners = new Map<string, Set<(message: WsPush) => void>>();
  private readonly latestPushByChannel = new Map<string, WsPush>();
  private readonly outboundQueue: Array<{ byteLength: number; message: string }> = [];
  private outboundQueueBytes = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionAttempt = 0;
  private pendingAuthReconnect = false;
  private disposed = false;
  private closedError: Error | null = null;
  private state: TransportState = "connecting";
  private heartbeatInFlight = false;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly url: string;
  private readonly unsubscribeAuthRefresh: () => void;

  constructor(url?: string) {
    const bridgeUrl = window.desktopBridge?.getWsUrl();
    const envUrl = import.meta.env.VITE_WS_URL as string | undefined;
    const operatorsHttpWsUrl = operatorsWsUrlFromHttpUrl(import.meta.env.VITE_OPERATORS_HTTP_URL);
    this.url =
      url ??
      (bridgeUrl && bridgeUrl.length > 0
        ? bridgeUrl
        : envUrl && envUrl.length > 0
          ? envUrl
          : (operatorsHttpWsUrl ??
            `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.hostname}:${window.location.port}`));
    this.unsubscribeAuthRefresh = onOperatorsAuthSessionRefreshed((session) => {
      if (!session) {
        return;
      }
      this.refreshAuthenticatedConnection();
    });
    window.addEventListener?.("online", this.handleWake);
    globalThis.document?.addEventListener?.("visibilitychange", this.handleVisibilityChange);
    this.connect();
  }

  async request<T = unknown>(
    method: string,
    params?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    if (typeof method !== "string" || method.length === 0) {
      throw new Error("Request method is required");
    }
    if (this.disposed) {
      return Promise.reject(this.closedError ?? new Error("Transport disposed"));
    }

    const id = String(this.nextId++);
    const body = params != null ? { ...params, _tag: method } : { _tag: method };
    const message: WsRequestEnvelope = { id, body };
    const encoded = JSON.stringify(message);
    const timeoutMs = options?.timeoutMs ?? REQUEST_TIMEOUT_MS;

    return new Promise<T>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.handleRequestTimeout(id, method);
      }, timeoutMs);

      this.pending.set(id, {
        resolve: resolve as (result: unknown) => void,
        reject,
        timeout,
      });

      this.send(encoded);
    });
  }

  subscribe<C extends WsPushChannel>(
    channel: C,
    listener: PushListener<C>,
    options?: SubscribeOptions,
  ): () => void {
    let channelListeners = this.listeners.get(channel);
    if (!channelListeners) {
      channelListeners = new Set<(message: WsPush) => void>();
      this.listeners.set(channel, channelListeners);
    }

    const wrappedListener = (message: WsPush) => {
      listener(message as WsPushMessage<C>);
    };
    channelListeners.add(wrappedListener);

    if (options?.replayLatest) {
      const latest = this.latestPushByChannel.get(channel);
      if (latest) {
        wrappedListener(latest);
      }
    }

    return () => {
      channelListeners?.delete(wrappedListener);
      if (channelListeners?.size === 0) {
        this.listeners.delete(channel);
      }
    };
  }

  getLatestPush<C extends WsPushChannel>(channel: C): WsPushMessage<C> | null {
    const latest = this.latestPushByChannel.get(channel);
    return latest ? (latest as WsPushMessage<C>) : null;
  }

  getState(): TransportState {
    return this.state;
  }

  dispose() {
    this.disposed = true;
    this.state = "disposed";
    const error = this.closedError ?? new Error("Transport disposed");
    this.closedError = error;
    this.unsubscribeAuthRefresh();
    window.removeEventListener?.("online", this.handleWake);
    globalThis.document?.removeEventListener?.("visibilitychange", this.handleVisibilityChange);
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.rejectPending(error);
    this.outboundQueue.length = 0;
    this.outboundQueueBytes = 0;
    this.ws?.close();
    this.ws = null;
  }

  private connect() {
    if (this.disposed) {
      return;
    }

    this.state = this.reconnectAttempt > 0 ? "reconnecting" : "connecting";
    const attempt = ++this.connectionAttempt;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    let opened = false;
    const isCurrentAttempt = () => attempt === this.connectionAttempt;

    ws.addEventListener("open", () => {
      if (!isCurrentAttempt()) {
        ws.close(1000, "Stale websocket connection");
        return;
      }
      opened = true;
      this.ws = ws;
      this.state = "open";
      this.reconnectAttempt = 0;
      this.flushQueue();
      this.scheduleHeartbeat();
    });

    ws.addEventListener("message", (event) => {
      if (!isCurrentAttempt()) {
        return;
      }
      this.handleMessage(event.data);
    });

    ws.addEventListener("close", (event) => {
      if (!isCurrentAttempt()) {
        return;
      }
      if (this.ws === ws) {
        this.ws = null;
      }
      this.stopHeartbeat();
      if (this.disposed) {
        this.state = "disposed";
        return;
      }
      if (event.code === OPERATORS_AUTH_EXPIRED_WS_CLOSE_CODE) {
        this.handleAuthExpired(event.reason || OPERATORS_AUTH_EXPIRED_MESSAGE);
        return;
      }
      if (opened) {
        this.rejectPending(new WsConnectionClosedError(event.reason));
        this.outboundQueue.length = 0;
        this.outboundQueueBytes = 0;
      }
      this.state = "closed";
      if (!opened) {
        void this.verifyAuthenticationBeforeReconnect();
        return;
      }
      this.scheduleReconnect();
    });

    ws.addEventListener("error", (event) => {
      if (!isCurrentAttempt()) {
        return;
      }
      // Log WebSocket errors for debugging (close event will follow)
      console.warn("WebSocket connection error", { type: event.type, url: this.url });
    });
  }

  private handleMessage(raw: unknown) {
    const result = decodeWsResponse(raw);
    if (Result.isFailure(result)) {
      console.warn("Dropped inbound WebSocket envelope", formatSchemaError(result.failure));
      return;
    }

    const message = result.success;
    if (isWsPushMessage(message)) {
      this.latestPushByChannel.set(message.channel, message);
      const channelListeners = this.listeners.get(message.channel);
      if (channelListeners) {
        for (const listener of channelListeners) {
          try {
            listener(message);
          } catch {
            // Swallow listener errors
          }
        }
      }
      return;
    }

    if (!isWebSocketResponseEnvelope(message)) {
      return;
    }

    const pending = this.takePendingRequest(message.id);
    if (!pending) {
      return;
    }

    if (message.error) {
      pending.reject(new WsRequestError(message.error.message, message.error.code));
      this.refreshAuthenticatedConnectionAfterPendingSettles();
      return;
    }

    pending.resolve(message.result);
    this.refreshAuthenticatedConnectionAfterPendingSettles();
  }

  private takePendingRequest(id: string): PendingRequest | null {
    const pending = this.pending.get(id);
    if (!pending) {
      return null;
    }

    clearTimeout(pending.timeout);
    this.pending.delete(id);
    return pending;
  }

  private handleRequestTimeout(id: string, method: string): void {
    const pending = this.takePendingRequest(id);
    if (!pending) {
      return;
    }

    const error = new WsConnectionClosedError(`Request timed out: ${method}`);
    pending.reject(error);
    this.replaceConnection(error);
  }

  private readonly handleVisibilityChange = () => {
    if (globalThis.document?.visibilityState === "visible") {
      this.handleWake();
    }
  };

  private readonly handleWake = () => {
    if (this.disposed) {
      return;
    }
    if (this.state !== "open" || this.ws?.readyState !== WebSocket.OPEN) {
      this.replaceConnection(new WsConnectionClosedError("Connection was unavailable on wake"));
      return;
    }
    void this.runHeartbeat();
  };

  private scheduleHeartbeat(delayMs = HEARTBEAT_INTERVAL_MS): void {
    if (this.disposed || this.heartbeatTimer !== null) {
      return;
    }
    this.heartbeatTimer = setTimeout(() => {
      this.heartbeatTimer = null;
      void this.runHeartbeat();
    }, delayMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.heartbeatInFlight = false;
  }

  private async runHeartbeat(): Promise<void> {
    if (
      this.disposed ||
      this.heartbeatInFlight ||
      this.state !== "open" ||
      this.ws?.readyState !== WebSocket.OPEN
    ) {
      return;
    }
    this.heartbeatInFlight = true;
    try {
      await this.request(WS_METHODS.serverPing, undefined, { timeoutMs: HEARTBEAT_TIMEOUT_MS });
    } catch {
      // Request timeout and close handling replace the suspect connection.
    } finally {
      this.heartbeatInFlight = false;
      this.scheduleHeartbeat();
    }
  }

  private replaceConnection(error: Error): void {
    if (this.disposed) {
      return;
    }
    const staleSocket = this.ws;
    this.ws = null;
    this.connectionAttempt += 1;
    this.state = "closed";
    this.pendingAuthReconnect = false;
    this.stopHeartbeat();
    this.rejectPending(error);
    this.outboundQueue.length = 0;
    this.outboundQueueBytes = 0;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    staleSocket?.close(1012, "Replacing stale connection");
    this.reconnectAttempt = 0;
    this.connect();
  }

  private send(encodedMessage: string) {
    if (this.disposed) {
      return;
    }

    const encodedBytes = new TextEncoder().encode(encodedMessage).byteLength;
    if (encodedBytes > MAX_OUTBOUND_ENVELOPE_BYTES) {
      this.replaceConnection(new WsConnectionClosedError("Outbound message exceeded its bound"));
      return;
    }
    if (
      this.outboundQueue.length + 1 > MAX_OUTBOUND_QUEUE_MESSAGES ||
      this.outboundQueueBytes + encodedBytes > MAX_OUTBOUND_QUEUE_BYTES
    ) {
      this.replaceConnection(new WsConnectionClosedError("Outbound queue exceeded its bound"));
      return;
    }
    this.outboundQueue.push({ byteLength: encodedBytes, message: encodedMessage });
    this.outboundQueueBytes += encodedBytes;
    try {
      this.flushQueue();
    } catch {
      // Swallow: flushQueue has queued the message for retry on reconnect
    }
  }

  private flushQueue() {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      return;
    }

    while (this.outboundQueue.length > 0) {
      const queued = this.outboundQueue[0];
      if (!queued) {
        return;
      }
      if (this.ws.bufferedAmount + queued.byteLength > MAX_SOCKET_BUFFERED_AMOUNT_BYTES) {
        this.replaceConnection(
          new WsConnectionClosedError("WebSocket send buffer exceeded its bound"),
        );
        return;
      }
      this.outboundQueue.shift();
      this.outboundQueueBytes = Math.max(0, this.outboundQueueBytes - queued.byteLength);
      try {
        this.ws.send(queued.message);
      } catch (error) {
        this.outboundQueue.unshift(queued);
        this.outboundQueueBytes += queued.byteLength;
        throw asError(error, "Failed to send WebSocket request.");
      }
    }
  }

  private scheduleReconnect() {
    if (this.disposed || this.reconnectTimer !== null) {
      return;
    }

    const delay =
      RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)] ??
      RECONNECT_DELAYS_MS[0]!;

    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private rejectPending(error: Error) {
    for (const pending of this.pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.pending.clear();
  }

  private handleAuthExpired(message: string) {
    const error = new OperatorsAuthExpiredError(message);
    notifyOperatorsAuthExpired(message);
    this.closedError = error;
    this.disposed = true;
    this.state = "disposed";
    this.pendingAuthReconnect = false;
    this.outboundQueue.length = 0;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.unsubscribeAuthRefresh();
    this.rejectPending(error);
    this.ws?.close();
    this.ws = null;
  }

  private async verifyAuthenticationBeforeReconnect() {
    try {
      const session = await fetchCurrentOperatorsSession({ notifyOnUnauthorized: true });
      if (!session) {
        this.handleAuthExpired(OPERATORS_AUTH_EXPIRED_MESSAGE);
        return;
      }
    } catch (error) {
      if (isOperatorsAuthExpiredError(error)) {
        this.handleAuthExpired(error.message);
        return;
      }
    }

    this.scheduleReconnect();
  }

  private refreshAuthenticatedConnection() {
    if (this.disposed) {
      return;
    }

    if (this.pending.size > 0) {
      this.pendingAuthReconnect = true;
      return;
    }

    this.pendingAuthReconnect = false;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const previousSocket = this.ws;
    this.ws = null;
    this.reconnectAttempt = 0;
    this.connect();
    previousSocket?.close(1000, "Refreshing authentication");
  }

  private refreshAuthenticatedConnectionAfterPendingSettles() {
    if (!this.pendingAuthReconnect || this.pending.size > 0) {
      return;
    }

    this.refreshAuthenticatedConnection();
  }
}
